import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import API from '../api';
import { logDownload } from '../utils/eventLog';
import FileDropZone from '../components/FileDropZone';
import { SysMailAddressForm } from '../components/SysMailAddress';

// ── 시스템(자체) 메일 화면 — 가비아 웹메일 형식 ─────────────────────────────
// 환경설정 > 회사정보에서 "시스템메일"을 선택했을 때 전자메일 메뉴에 표시된다.
// 폴더: 받은메일함 / 보낸메일함 / 임시보관함 / 예약메일함 / 스팸메일함 / 휴지통
// 기능: 서명, 작성 기본값, 임시저장(자동 포함), 수신확인, 스팸 자동분류·신고·차단, 예약발송, 원본(.eml) 다운로드.
// 받은 메일 HTML은 스크립트가 실행되지 않는 sandbox iframe 안에서만 보여주고, 외부 이미지는 사용자가 허용하기 전까지 막는다.

const FOLDERS = [
  { key: 'inbox', label: '받은메일함' },
  { key: 'sent', label: '보낸메일함' },
  { key: 'drafts', label: '임시보관함' },
  { key: 'scheduled', label: '예약메일함' },
  { key: 'spam', label: '스팸메일함' },
  { key: 'trash', label: '휴지통' },
];
const B = '#185FA5';
const btn = (bg = B, fg = '#fff') => ({ padding: '7px 14px', background: bg, color: fg, border: bg === '#fff' ? '1px solid #d1d5db' : 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 });
const ghost = { padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 12.5 };
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13.5, boxSizing: 'border-box', outline: 'none' };

const errMsg = (e, d = '처리하지 못했습니다.') => e?.response?.data?.message || d;
const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(String(s).includes('T') || /Z$/.test(String(s)) ? s : String(s).replace(' ', 'T'));
  if (isNaN(d)) return String(s);
  const n = new Date(); const p = (x) => String(x).padStart(2, '0');
  if (d.toDateString() === n.toDateString()) return `${p(d.getHours())}:${p(d.getMinutes())}`;
  if (d.getFullYear() === n.getFullYear()) return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const fmtFull = (s) => { if (!s) return ''; const d = new Date(s); if (isNaN(d)) return String(s); const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`; };
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)}MB` : n > 1024 ? `${Math.round(n / 1024)}KB` : `${n || 0}B`);
const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const addrText = (a) => (a ? (a.name ? `${a.name} <${a.address}>` : a.address) : '');
const toLocalInput = (d) => { const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

// 받은 메일 본문 — sandbox iframe(스크립트 금지) + CSP(원격 이미지 차단 옵션)
function MailBody({ html, text, allowRemote }) {
  const ref = useRef(null);
  const doc = useMemo(() => {
    const csp = `default-src 'none'; img-src data: ${allowRemote ? 'https: http:' : ''}; style-src 'unsafe-inline'; font-src data:`;
    const body = html || `<pre style="white-space:pre-wrap;font-family:inherit">${esc(text)}</pre>`;
    return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><base target="_blank"><style>body{margin:0;padding:4px;font:14px/1.6 'Malgun Gothic',sans-serif;color:#222;word-break:break-word}img{max-width:100%;height:auto}blockquote{margin:6px 0 6px 4px;padding-left:10px;border-left:3px solid #ddd;color:#555}</style></head><body>${body}</body></html>`;
  }, [html, text, allowRemote]);
  const fit = () => { try { const d = ref.current.contentDocument; if (d && d.body) ref.current.style.height = `${Math.max(d.documentElement.scrollHeight, 120)}px`; } catch (e) { /* ignore */ } };
  return <iframe ref={ref} title="mail" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={doc} onLoad={fit} style={{ width: '100%', border: 'none', minHeight: 120, display: 'block' }} />;
}

// ── 주소 입력(쉼표 구분 + 자동완성) ─────────────────────────────────────
function AddrInput({ value, onChange, contacts, placeholder }) {
  const [focus, setFocus] = useState(false);
  const last = (value.split(/[,;]/).pop() || '').trim().toLowerCase();
  const sugg = useMemo(() => {
    if (!focus || last.length < 1) return [];
    return contacts.filter((c) => (c.address || '').toLowerCase().includes(last) || (c.name || '').toLowerCase().includes(last)).slice(0, 8);
  }, [contacts, last, focus]);
  const pick = (c) => {
    const parts = value.split(/[,;]/); parts.pop();
    parts.push(` ${c.name ? `${c.name} <${c.address}>` : c.address}`);
    onChange(`${parts.map((x) => x.trim()).filter(Boolean).join(', ')}, `);
  };
  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <input style={inp} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)} />
      {sugg.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, zIndex: 20, boxShadow: '0 4px 14px rgba(0,0,0,0.12)', maxHeight: 220, overflow: 'auto' }}>
          {sugg.map((c, i) => (
            <div key={i} onMouseDown={(e) => { e.preventDefault(); pick(c); }} style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
              <b>{c.name || c.address}</b> <span style={{ color: '#6b7280' }}>{c.name ? c.address : ''}{c.sub ? ` · ${c.sub}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Overlay({ title, width = 640, onClose, children, z = 3000 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: z, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, width, maxWidth: '95vw', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── 편집기(contentEditable) ─────────────────────────────────────────────
function Editor({ editorRef, initialHtml }) {
  useEffect(() => { if (editorRef.current) editorRef.current.innerHTML = initialHtml || ''; /* 최초 1회만 */ // eslint-disable-next-line
  }, []);
  const cmd = (c, v) => { editorRef.current && editorRef.current.focus(); document.execCommand(c, false, v); };
  const tb = (label, c, v, extra) => <button type="button" key={label} title={label} onMouseDown={(e) => { e.preventDefault(); cmd(c, v); }} style={{ ...ghost, padding: '3px 8px', ...extra }}>{label}</button>;
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []).filter((i) => i.type.startsWith('image/'));
    if (!items.length) return;
    e.preventDefault();
    items.forEach((it) => {
      const f = it.getAsFile(); if (!f || f.size > 5 * 1024 * 1024) return;
      const r = new FileReader(); r.onload = () => document.execCommand('insertImage', false, r.result); r.readAsDataURL(f);
    });
  };
  const link = () => { const u = window.prompt('링크 주소 (https://...)'); if (u && /^(https?:|mailto:)/i.test(u)) cmd('createLink', u); };
  return (
    <div style={{ border: '1px solid #d1d5db', borderRadius: 6 }}>
      <div style={{ display: 'flex', gap: 4, padding: 6, borderBottom: '1px solid #e5e7eb', background: '#f9fafb', flexWrap: 'wrap', alignItems: 'center' }}>
        {tb('B', 'bold', null, { fontWeight: 800 })}{tb('I', 'italic', null, { fontStyle: 'italic' })}{tb('U', 'underline', null, { textDecoration: 'underline' })}
        {tb('• 목록', 'insertUnorderedList')}{tb('1. 목록', 'insertOrderedList')}
        {tb('좌', 'justifyLeft')}{tb('중', 'justifyCenter')}{tb('우', 'justifyRight')}
        <select onChange={(e) => { if (e.target.value) cmd('fontSize', e.target.value); e.target.value = ''; }} defaultValue="" style={{ ...ghost, padding: '3px 4px' }}>
          <option value="">크기</option><option value="2">작게</option><option value="3">보통</option><option value="5">크게</option><option value="6">아주 크게</option>
        </select>
        <input type="color" title="글자색" onChange={(e) => cmd('foreColor', e.target.value)} style={{ width: 28, height: 26, padding: 0, border: '1px solid #d1d5db', borderRadius: 4 }} />
        <button type="button" onMouseDown={(e) => { e.preventDefault(); link(); }} style={{ ...ghost, padding: '3px 8px' }}>링크</button>
        {tb('서식해제', 'removeFormat')}
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning onPaste={onPaste}
        style={{ minHeight: 240, maxHeight: '38vh', overflow: 'auto', padding: 12, fontSize: 14, lineHeight: 1.6, outline: 'none' }} />
    </div>
  );
}

// ── 메일 쓰기 ─────────────────────────────────────────────────────────
function Compose({ init, settings, contacts, onClose, onDone, showMsg }) {
  const editorRef = useRef(null);
  const [to, setTo] = useState(init.to || '');
  const [cc, setCc] = useState(init.cc || '');
  const [bcc, setBcc] = useState(init.bcc || '');
  const [showCc, setShowCc] = useState(!!(init.cc || init.bcc));
  const [subject, setSubject] = useState(init.subject || '');
  const [receipt, setReceipt] = useState(init.receipt != null ? !!init.receipt : !!settings.receipt_default);
  const [files, setFiles] = useState([]);
  const [kept, setKept] = useState(init.attachments || []);
  const [draftId, setDraftId] = useState(init.draftId || null);
  const [sched, setSched] = useState(init.scheduleAt || '');
  const [showSched, setShowSched] = useState(!!init.scheduleAt);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState('');
  const dirtyRef = useRef(false);
  const stateRef = useRef({});
  stateRef.current = { to, cc, bcc, subject, receipt, files, kept, draftId, sched };

  // 본문 초기값: 임시보관/답장은 그대로, 새 메일은 기본값 + 서명
  const initialHtml = useMemo(() => {
    if (init.html != null) return init.html;
    const sig = settings.signature_on && settings.signature_html ? `<br><div data-sig="1">${settings.signature_html}</div>` : '';
    return `${settings.default_body_html || ''}${sig}`;
    // eslint-disable-next-line
  }, []);
  const initialWithSig = useMemo(() => {
    if (init.html == null) return initialHtml;
    if (init.addSignature && settings.signature_on && settings.signature_html) return `<br><br>${`<div data-sig="1">${settings.signature_html}</div>`}${initialHtml}`;
    return initialHtml;
    // eslint-disable-next-line
  }, []);

  const mark = () => { dirtyRef.current = true; };
  const build = (mode, extra = {}) => {
    const s = stateRef.current; const fd = new FormData();
    fd.append('mode', mode); fd.append('to', s.to); fd.append('cc', s.cc); fd.append('bcc', s.bcc); fd.append('subject', s.subject);
    fd.append('html', editorRef.current ? editorRef.current.innerHTML : ''); fd.append('receipt', s.receipt ? '1' : '0');
    if (s.draftId) { fd.append('draftId', String(s.draftId)); fd.append('keep', JSON.stringify(s.kept.map((k) => k.file))); }
    if (init.inReplyTo) fd.append('inReplyTo', init.inReplyTo);
    if (init.references) fd.append('references', init.references);
    s.files.forEach((f) => fd.append('files', f));
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    return fd;
  };
  const post = (fd) => API.post('/sysmail/send', fd, { headers: { 'Content-Type': 'multipart/form-data' } });

  const saveDraft = async (silent) => {
    setBusy(true);
    try {
      const r = await post(build('draft'));
      setDraftId(r.data.id); dirtyRef.current = false; setFiles([]);
      // 저장 후 첨부는 서버에 보관되므로 목록을 다시 읽는다
      try { const d = await API.get(`/sysmail/messages/${r.data.id}`); setKept(d.data.compose.attachments || []); } catch (e) { /* ignore */ }
      const t = fmtDate(new Date().toISOString()); setSavedAt(t);
      if (!silent) showMsg('임시보관함에 저장했습니다.');
      onDone && onDone(false);
    } catch (e) { if (!silent) showMsg(errMsg(e), 'error'); }
    setBusy(false);
  };
  // 60초마다 변경이 있으면 자동 임시저장
  useEffect(() => {
    const t = setInterval(() => { if (dirtyRef.current && !busy) saveDraft(true); }, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [busy]);

  const send = async () => {
    if (!to.trim() && !cc.trim() && !bcc.trim()) { showMsg('받는 사람을 입력하세요.', 'error'); return; }
    if (!subject.trim() && !window.confirm('제목이 없습니다. 그대로 보낼까요?')) return;
    let mode = 'send'; const extra = {};
    if (showSched && sched) {
      const t = new Date(sched);
      if (isNaN(t) || t.getTime() <= Date.now()) { showMsg('예약 시각은 현재보다 이후여야 합니다.', 'error'); return; }
      mode = 'schedule'; extra.scheduleAt = sched;
    }
    setBusy(true);
    try {
      const r = await post(build(mode, extra));
      if (mode === 'schedule') showMsg(`${sched.replace('T', ' ')} 에 발송되도록 예약했습니다.`);
      else if (r.data.failures && r.data.failures.length) showMsg(`일부 받는 사람에게 전달하지 못했습니다: ${r.data.failures.map((f) => f.addr || f.address || f).join(', ')}`, 'error');
      else showMsg('메일을 보냈습니다.');
      onDone && onDone(true);
    } catch (e) {
      if (e?.response?.data?.draftId) setDraftId(e.response.data.draftId);
      showMsg(errMsg(e, '발송하지 못했습니다. 임시보관함에 저장되었습니다.'), 'error'); onDone && onDone(false);
    }
    setBusy(false);
  };

  const row = (label, node) => <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}><div style={{ width: 62, fontSize: 13, color: '#555' }}>{label}</div>{node}</div>;
  return (
    <Overlay title="메일 쓰기" width={880} onClose={() => onClose()}>
      <div onInput={mark}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button disabled={busy} onClick={send} style={btn()}>{showSched && sched ? '예약발송' : '보내기'}</button>
          <button disabled={busy} onClick={() => saveDraft(false)} style={btn('#fff', '#374151')}>임시저장</button>
          <label style={{ fontSize: 13, display: 'flex', gap: 4, alignItems: 'center', marginLeft: 6 }}><input type="checkbox" checked={receipt} onChange={(e) => { setReceipt(e.target.checked); mark(); }} />수신확인</label>
          <label style={{ fontSize: 13, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={showSched} onChange={(e) => { setShowSched(e.target.checked); if (e.target.checked && !sched) setSched(toLocalInput(new Date(Date.now() + 3600000))); }} />예약발송</label>
          {showSched && <input type="datetime-local" value={sched} min={toLocalInput(new Date())} onChange={(e) => { setSched(e.target.value); mark(); }} style={{ ...inp, width: 200 }} />}
          {savedAt && <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 'auto' }}>{savedAt} 임시저장됨</span>}
        </div>
        {row('받는사람', <><AddrInput value={to} onChange={(v) => { setTo(v); mark(); }} contacts={contacts} placeholder="주소를 쉼표(,)로 구분해 입력" />{!showCc && <button type="button" onClick={() => setShowCc(true)} style={ghost}>참조/숨은참조</button>}</>)}
        {showCc && row('참조', <AddrInput value={cc} onChange={(v) => { setCc(v); mark(); }} contacts={contacts} />)}
        {showCc && row('숨은참조', <AddrInput value={bcc} onChange={(v) => { setBcc(v); mark(); }} contacts={contacts} />)}
        {row('제목', <input style={inp} value={subject} onChange={(e) => { setSubject(e.target.value); mark(); }} />)}
        <Editor editorRef={editorRef} initialHtml={initialWithSig} />
        <div style={{ marginTop: 10 }}>
          {kept.length > 0 && (
            <div style={{ marginBottom: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {kept.map((k) => <span key={k.file} style={{ background: '#eef2f7', borderRadius: 14, padding: '3px 10px', fontSize: 12.5 }}>📎 {k.filename} <b style={{ cursor: 'pointer', marginLeft: 4 }} onClick={() => { setKept((a) => a.filter((x) => x.file !== k.file)); mark(); }}>✕</b></span>)}
            </div>
          )}
          {files.length > 0 && (
            <div style={{ marginBottom: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {files.map((f, i) => <span key={i} style={{ background: '#eef2f7', borderRadius: 14, padding: '3px 10px', fontSize: 12.5 }}>📎 {f.name} ({fmtSize(f.size)}) <b style={{ cursor: 'pointer', marginLeft: 4 }} onClick={() => { setFiles((a) => a.filter((_, j) => j !== i)); mark(); }}>✕</b></span>)}
            </div>
          )}
          <FileDropZone multiple fileNames={files.map((f) => f.name)} onFiles={(fs) => { setFiles((a) => [...a, ...fs].slice(0, 10)); mark(); }} style={{ marginBottom: 0, padding: '10px 12px' }} />
        </div>
      </div>
    </Overlay>
  );
}

// ── 환경설정(서명/기본값/차단목록) ────────────────────────────────────
function SettingsModal({ settings, onClose, onSaved, showMsg }) {
  const sigRef = useRef(null); const defRef = useRef(null);
  const [on, setOn] = useState(settings.signature_on !== 0);
  const [rc, setRc] = useState(!!settings.receipt_default);
  const [block, setBlock] = useState([]); const [pat, setPat] = useState('');
  const loadBlock = useCallback(() => { API.get('/sysmail/blocklist').then((r) => setBlock(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { loadBlock(); }, [loadBlock]);
  const save = async () => {
    try {
      const body = { signature_html: sigRef.current.innerHTML, signature_on: on ? 1 : 0, default_body_html: defRef.current.innerHTML, receipt_default: rc ? 1 : 0 };
      await API.put('/sysmail/settings', body); onSaved(body); showMsg('메일 환경설정을 저장했습니다.'); onClose();
    } catch (e) { showMsg(errMsg(e), 'error'); }
  };
  const add = async () => { if (!pat.trim()) return; try { await API.post('/sysmail/blocklist', { pattern: pat.trim() }); setPat(''); loadBlock(); } catch (e) { showMsg(errMsg(e), 'error'); } };
  return (
    <Overlay title="메일 환경설정" width={760} onClose={onClose}>
      <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 6 }}>서명 <label style={{ fontWeight: 400, marginLeft: 10, fontSize: 13 }}><input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} /> 새 메일에 자동 삽입</label></div>
      <Editor editorRef={sigRef} initialHtml={settings.signature_html} />
      <div style={{ fontWeight: 700, fontSize: 13.5, margin: '14px 0 6px' }}>작성 내용 기본값 <span style={{ fontWeight: 400, color: '#6b7280' }}>(새 메일 본문에 미리 채워집니다)</span></div>
      <Editor editorRef={defRef} initialHtml={settings.default_body_html} />
      <label style={{ display: 'block', fontSize: 13, margin: '12px 0' }}><input type="checkbox" checked={rc} onChange={(e) => setRc(e.target.checked)} /> 새 메일 작성 시 수신확인을 기본으로 켬</label>
      <div style={{ fontWeight: 700, fontSize: 13.5, margin: '10px 0 6px' }}>수신 차단 목록 <span style={{ fontWeight: 400, color: '#6b7280' }}>(주소 또는 @도메인 — 해당 메일은 스팸함으로 이동)</span></div>
      <div style={{ display: 'flex', gap: 6 }}><input style={inp} value={pat} onChange={(e) => setPat(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} /><button onClick={add} style={btn()}>추가</button></div>
      <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {block.map((b) => <span key={b.id} style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 14, padding: '3px 10px', fontSize: 12.5 }}>{b.pattern} <b style={{ cursor: 'pointer' }} onClick={() => API.delete(`/sysmail/blocklist/${b.id}`).then(loadBlock)}>✕</b></span>)}
        {!block.length && <span style={{ fontSize: 12.5, color: '#9ca3af' }}>차단한 항목이 없습니다.</span>}
      </div>
      <div style={{ textAlign: 'right', marginTop: 16 }}><button onClick={onClose} style={{ ...btn('#fff', '#374151'), marginRight: 6 }}>닫기</button><button onClick={save} style={btn()}>저장</button></div>
    </Overlay>
  );
}

// ── 본 화면 ──────────────────────────────────────────────────────────
export default function SysMail({ status, onSwitchExternal, onStatusChange }) {
  const [folder, setFolder] = useState('inbox');
  const [counts, setCounts] = useState({});
  const [list, setList] = useState({ items: [], total: 0, page: 1, size: 30 });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState(''); const [qIn, setQIn] = useState('');
  const [fl, setFl] = useState({ unread: false, flagged: false, att: false });
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(null); // 열린 메일 상세
  const [remote, setRemote] = useState(false);
  const [loading, setLoading] = useState(false);
  const [compose, setCompose] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSet, setShowSet] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [msg, setMsg] = useState(null);
  const [needBox, setNeedBox] = useState(!status.address);
  const msgTimer = useRef(null);

  const showMsg = useCallback((text, type = 'success') => { setMsg({ text, type }); clearTimeout(msgTimer.current); msgTimer.current = setTimeout(() => setMsg(null), 3500); }, []);
  useEffect(() => () => clearTimeout(msgTimer.current), []);

  const loadCounts = useCallback(() => { API.get('/sysmail/folders').then((r) => setCounts(r.data || {})).catch(() => {}); }, []);
  const loadList = useCallback(() => {
    setLoading(true);
    API.get('/sysmail/messages', { params: { folder, page, size: 30, q, unread: fl.unread ? 1 : 0, flagged: fl.flagged ? 1 : 0, att: fl.att ? 1 : 0 } })
      .then((r) => { setList(r.data); setSel([]); })
      .catch((e) => { if (e?.response?.data?.code === 'NO_MAILBOX') setNeedBox(true); else showMsg(errMsg(e), 'error'); })
      .finally(() => setLoading(false));
  }, [folder, page, q, fl, showMsg]);

  useEffect(() => { if (!needBox) { loadList(); loadCounts(); } }, [loadList, loadCounts, needBox]);
  const [refreshMs, setRefreshMs] = useState(5 * 60 * 1000);
  useEffect(() => { API.get('/sysmail/refresh-interval').then(r => { const m = Number(r.data && r.data.minutes); if (m >= 1 && m <= 120) setRefreshMs(m * 60 * 1000); }).catch(() => {}); }, []);
  useEffect(() => { if (needBox) return undefined; const t = setInterval(() => { loadCounts(); if (folder === 'inbox' && page === 1 && !q) loadList(); }, refreshMs); return () => clearInterval(t); }, [needBox, loadCounts, loadList, folder, page, q, refreshMs]);
  useEffect(() => {
    if (needBox) return;
    API.get('/sysmail/settings').then((r) => setSettings(r.data)).catch(() => setSettings({ signature_html: '', signature_on: 1, default_body_html: '', receipt_default: 0 }));
    Promise.all([API.get('/sysmail/directory').catch(() => ({ data: [] })), API.get('/email/contacts').catch(() => ({ data: [] }))]).then(([d, c]) => {
      const seen = new Set(); const out = [];
      (d.data || []).forEach((x) => { if (x.address && !seen.has(x.address)) { seen.add(x.address); out.push({ name: x.name, address: x.address, sub: [x.department, x.position].filter(Boolean).join(' ') }); } });
      (c.data || []).forEach((x) => { const a = (x.email || '').toLowerCase(); if (a && !seen.has(a)) { seen.add(a); out.push({ name: x.name || '', address: a, sub: x.company || '' }); } });
      setContacts(out);
    });
  }, [needBox]);

  const changeFolder = (f) => { setFolder(f); setPage(1); setOpen(null); setSel([]); setQ(''); setQIn(''); setFl({ unread: false, flagged: false, att: false }); };

  const openMsg = async (m) => {
    try {
      const r = await API.get(`/sysmail/messages/${m.id}`);
      if (r.data.compose) { // 임시보관/예약 → 작성창으로
        const c = r.data.compose;
        setCompose({ to: (c.to || []).map(addrText).join(', '), cc: (c.cc || []).map(addrText).join(', '), bcc: (c.bcc || []).map(addrText).join(', '), subject: c.subject, html: c.html, receipt: c.receipt, attachments: c.attachments, draftId: r.data.id, inReplyTo: c.inReplyTo, references: c.references,
          scheduleAt: r.data.folder === 'scheduled' && r.data.scheduled_at ? r.data.scheduled_at.replace(' ', 'T').slice(0, 16) : '' });
        return;
      }
      setOpen(r.data); setRemote(false);
      if (!m.seen) { setList((l) => ({ ...l, items: l.items.map((x) => (x.id === m.id ? { ...x, seen: 1 } : x)) })); loadCounts(); }
    } catch (e) { showMsg(errMsg(e), 'error'); }
  };

  const ids = sel.length ? sel : (open ? [open.id] : []);
  const act = async (fn, okText) => { try { await fn(); if (okText) showMsg(okText); setOpen(null); loadList(); loadCounts(); } catch (e) { showMsg(errMsg(e), 'error'); } };
  const del = () => ids.length && act(() => API.post('/sysmail/messages/delete', { ids }), folder === 'trash' ? '완전히 삭제했습니다.' : '휴지통으로 이동했습니다.');
  const mark = (seen) => ids.length && act(() => API.post('/sysmail/messages/mark', { ids, seen }));
  const move = (to) => ids.length && act(() => API.post('/sysmail/messages/move', { ids, folder: to }), '이동했습니다.');
  const restore = () => ids.length && act(() => API.post('/sysmail/messages/restore', { ids }), '복원했습니다.');
  const spam = (isSpam, block) => {
    if (!ids.length) return;
    if (isSpam && block && !window.confirm('스팸으로 신고하고 보낸 사람을 차단할까요?')) return;
    act(() => Promise.all(ids.map((id) => API.post(`/sysmail/messages/${id}/spam`, { spam: isSpam, block: !!block }))), isSpam ? '스팸으로 신고했습니다.' : '스팸이 아님으로 처리했습니다.');
  };
  const empty = () => { if (!window.confirm(`${folder === 'spam' ? '스팸메일함' : '휴지통'}을 비울까요? 되돌릴 수 없습니다.`)) return; act(() => API.post(`/sysmail/folders/${folder}/empty`), '비웠습니다.'); };
  const cancelSched = (id) => act(() => API.post(`/sysmail/messages/${id}/cancel-schedule`), '예약을 취소하고 임시보관함으로 옮겼습니다.');
  const flag = async (m, e) => { e.stopPropagation(); try { await API.patch(`/sysmail/messages/${m.id}`, { flagged: m.flagged ? 0 : 1 }); setList((l) => ({ ...l, items: l.items.map((x) => (x.id === m.id ? { ...x, flagged: m.flagged ? 0 : 1 } : x)) })); } catch (er) { showMsg(errMsg(er), 'error'); } };

  const dl = async (url, name, log) => {
    try {
      const r = await API.get(url, { responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 3000);
      if (log) logDownload('전자메일', name);
    } catch (e) { showMsg('다운로드하지 못했습니다. (원본이 없는 메일일 수 있습니다)', 'error'); }
  };

  const newMail = () => setCompose({});
  const quote = (m) => `<br><br><blockquote>${esc(fmtFull(m.date))} ${esc(addrText(m.from))} 님이 작성:<br>${m.html || `<pre style="white-space:pre-wrap">${esc(m.text)}</pre>`}</blockquote>`;
  const reply = (all) => {
    const m = open; const me = (status.address || '').toLowerCase();
    const ccs = all ? [...m.to, ...m.cc].filter((a) => a.address !== me && a.address !== m.from.address) : [];
    setCompose({ to: m.from.address, cc: ccs.map(addrText).join(', '), subject: /^re:/i.test(m.subject) ? m.subject : `Re: ${m.subject}`, html: quote(m), addSignature: true, inReplyTo: m.message_id || '', references: [m.references, m.message_id].filter(Boolean).join(' ') });
  };
  const forward = () => { const m = open; setCompose({ subject: /^fwd?:/i.test(m.subject) ? m.subject : `Fwd: ${m.subject}`, html: quote(m), addSignature: true }); };

  const allChecked = list.items.length > 0 && sel.length === list.items.length;
  const pages = Math.max(1, Math.ceil(list.total / list.size));
  const isOutbox = folder === 'sent' || folder === 'drafts' || folder === 'scheduled';

  if (needBox) {
    return (
      <div style={{ maxWidth: 520, margin: '40px auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
        <h3 style={{ marginTop: 0 }}>메일주소 등록</h3>
        <div style={{ fontSize: 13.5, color: '#555', marginBottom: 14 }}>시스템메일을 사용하려면 먼저 내 메일주소를 등록해야 합니다. 등록한 주소는 IMS 전체에서 공유됩니다.</div>
        <SysMailAddressForm domain={status.domain} onSaved={() => { setNeedBox(false); onStatusChange && onStatusChange(); }} />
        {onSwitchExternal && <div style={{ marginTop: 14, textAlign: 'right' }}><button onClick={onSwitchExternal} style={ghost}>외부 메일 계정 보기</button></div>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 14, height: 'calc(100vh - 150px)', minHeight: 520 }}>
      {msg && <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: msg.type === 'error' ? '#dc2626' : '#16a34a', color: '#fff', padding: '10px 22px', borderRadius: 8, zIndex: 4000, fontSize: 14 }}>{msg.text}</div>}
      <div style={{ width: 190, flexShrink: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, overflow: 'auto' }}>
        <button onClick={newMail} style={{ ...btn(), width: '100%', padding: '10px 0', marginBottom: 10 }}>✉ 메일쓰기</button>
        {FOLDERS.map((f) => {
          const c = counts[f.key] || { total: 0, unseen: 0 };
          return (
            <div key={f.key} onClick={() => changeFolder(f.key)} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13.5, background: folder === f.key ? '#eaf3fc' : 'transparent', color: folder === f.key ? B : '#374151', fontWeight: folder === f.key ? 700 : 500 }}>
              <span>{f.label}</span>
              <span style={{ fontSize: 12, color: c.unseen ? '#dc2626' : '#9ca3af', fontWeight: c.unseen ? 700 : 400 }}>{['inbox', 'spam'].includes(f.key) ? (c.unseen || '') : (['drafts', 'scheduled'].includes(f.key) ? (c.total || '') : '')}</span>
            </div>
          );
        })}
        <div style={{ borderTop: '1px solid #eee', marginTop: 10, paddingTop: 10 }}>
          <button onClick={() => setShowSet(true)} style={{ ...ghost, width: '100%', marginBottom: 6 }}>⚙ 메일 환경설정</button>
          {onSwitchExternal && <button onClick={onSwitchExternal} style={{ ...ghost, width: '100%' }}>외부 메일 계정</button>}
        </div>
        <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 10, wordBreak: 'break-all' }}>내 주소<br /><b style={{ color: '#374151' }}>{status.address}</b></div>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}>
        {open ? (
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              <button onClick={() => { setOpen(null); }} style={ghost}>← 목록</button>
              {open.folder !== 'sent' && <><button onClick={() => reply(false)} style={ghost}>답장</button><button onClick={() => reply(true)} style={ghost}>전체답장</button></>}
              <button onClick={forward} style={ghost}>전달</button>
              <button onClick={del} style={ghost}>삭제</button>
              {['inbox', 'trash'].includes(open.folder) && <button onClick={() => spam(true, true)} style={ghost}>스팸신고·차단</button>}
              {open.folder === 'spam' && <button onClick={() => spam(false)} style={ghost}>스팸 아님</button>}
              {open.folder === 'trash' && <button onClick={restore} style={ghost}>복원</button>}
              <button onClick={() => mark(0)} style={ghost}>안읽음으로</button>
              <button onClick={() => dl(`/sysmail/messages/${open.id}/raw`, `${(open.subject || 'mail').replace(/[\\/:*?"<>|]/g, '_').slice(0, 80)}.eml`, true)} style={ghost}>원본 다운로드(.eml)</button>
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{open.subject || '(제목 없음)'}</h3>
            {open.folder === 'spam' && <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>스팸으로 분류된 메일입니다{open.spam_reason ? ` — ${open.spam_reason}` : ''}. 링크·첨부를 열 때 주의하세요.</div>}
            <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.8, borderBottom: '1px solid #eee', paddingBottom: 10, marginBottom: 10 }}>
              <div>보낸사람 : <b>{addrText(open.from)}</b> <span style={{ color: '#9ca3af' }}>· {fmtFull(open.date)}</span></div>
              <div>받는사람 : {open.to.map(addrText).join(', ') || '-'}</div>
              {open.cc.length > 0 && <div>참조 : {open.cc.map(addrText).join(', ')}</div>}
              {open.bcc.length > 0 && <div>숨은참조 : {open.bcc.map((b) => (typeof b === 'string' ? b : addrText(b))).join(', ')}</div>}
            </div>
            {open.attachments.length > 0 && (
              <div style={{ marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {open.attachments.map((a) => <button key={a.idx} onClick={() => dl(`/sysmail/messages/${open.id}/attachments/${a.idx}`, a.filename, true)} style={{ ...ghost, background: '#f8fafc' }}>📎 {a.filename} ({fmtSize(a.size)})</button>)}
              </div>
            )}
            {open.has_remote_images && !remote && <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 12px', fontSize: 12.5, marginBottom: 8 }}>보안을 위해 외부 이미지를 차단했습니다. <button onClick={() => setRemote(true)} style={{ ...ghost, marginLeft: 6 }}>이미지 보기</button></div>}
            <MailBody html={open.html} text={open.text} allowRemote={remote} />
            {open.folder === 'sent' && open.receipt_requested && (
              <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 6 }}>수신확인</div>
                <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
                  <tbody>
                    {(open.receipts || []).map((r, i) => <tr key={i}><td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6' }}>{r.recipient}</td><td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', color: r.opened_at ? '#15803d' : '#9ca3af' }}>{r.opened_at ? `읽음 (${fmtFull(r.opened_at)})` : '아직 읽지 않음'}</td></tr>)}
                    {!(open.receipts || []).length && <tr><td style={{ color: '#9ca3af', padding: 6 }}>확인 대상이 없습니다.</td></tr>}
                  </tbody>
                </table>
                <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4 }}>외부 수신자는 상대 메일 프로그램이 이미지 로딩/수신확인 응답을 허용한 경우에만 확인됩니다.</div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 6, padding: 10, borderBottom: '1px solid #eee', alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="checkbox" checked={allChecked} onChange={(e) => setSel(e.target.checked ? list.items.map((m) => m.id) : [])} />
              {folder === 'trash' ? <><button disabled={!sel.length} onClick={restore} style={ghost}>복원</button><button disabled={!sel.length} onClick={del} style={ghost}>완전삭제</button><button onClick={empty} style={ghost}>휴지통 비우기</button></>
                : folder === 'spam' ? <><button disabled={!sel.length} onClick={() => spam(false)} style={ghost}>스팸 아님</button><button disabled={!sel.length} onClick={del} style={ghost}>삭제</button><button onClick={empty} style={ghost}>스팸함 비우기</button></>
                : <><button disabled={!sel.length} onClick={del} style={ghost}>삭제</button>
                  {folder === 'inbox' && <button disabled={!sel.length} onClick={() => spam(true, true)} style={ghost}>스팸신고</button>}
                  {folder === 'inbox' && <><button disabled={!sel.length} onClick={() => mark(1)} style={ghost}>읽음</button><button disabled={!sel.length} onClick={() => mark(0)} style={ghost}>안읽음</button></>}</>}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                {[['unread', '안읽음'], ['flagged', '별표'], ['att', '첨부']].map(([k, l]) => <label key={k} style={{ fontSize: 12.5 }}><input type="checkbox" checked={fl[k]} onChange={(e) => { setFl((f) => ({ ...f, [k]: e.target.checked })); setPage(1); }} /> {l}</label>)}
                <input style={{ ...inp, width: 190 }} placeholder="제목/보낸사람 검색" value={qIn} onChange={(e) => setQIn(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setQ(qIn.trim()); setPage(1); } }} />
                <button onClick={() => { setQ(qIn.trim()); setPage(1); }} style={btn()}>검색</button>
                <button onClick={() => { loadList(); loadCounts(); }} style={ghost}>새로고침</button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {loading && !list.items.length && <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af' }}>불러오는 중...</div>}
              {!loading && !list.items.length && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>메일이 없습니다.</div>}
              {list.items.map((m) => (
                <div key={m.id} onClick={() => openMsg(m)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: sel.includes(m.id) ? '#eef6ff' : '#fff', fontWeight: m.seen || isOutbox ? 400 : 700 }}>
                  <input type="checkbox" checked={sel.includes(m.id)} onClick={(e) => e.stopPropagation()} onChange={(e) => setSel((s) => (e.target.checked ? [...s, m.id] : s.filter((x) => x !== m.id)))} />
                  <span onClick={(e) => flag(m, e)} style={{ cursor: 'pointer', color: m.flagged ? '#f59e0b' : '#d1d5db', fontSize: 16 }}>{m.flagged ? '★' : '☆'}</span>
                  <span style={{ width: 170, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13.5 }}>
                    {isOutbox ? (m.to.map((a) => a.name || a.address || a).join(', ') || '(받는사람 없음)') : (m.from_name || m.from_addr)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13.5 }}>
                    {m.send_status === 'failed' && <span style={{ color: '#dc2626', marginRight: 6 }}>[발송실패]</span>}
                    {m.subject || '(제목 없음)'} <span style={{ color: '#9ca3af', fontWeight: 400 }}>{m.snippet ? ` - ${m.snippet}` : ''}</span>
                  </span>
                  {folder === 'sent' && m.receipt_req ? <span style={{ fontSize: 11.5, color: m.rc_opened ? '#15803d' : '#9ca3af', whiteSpace: 'nowrap' }}>{m.rc_opened ? `읽음 ${m.rc_opened}/${m.rc_total}` : '미확인'}</span> : null}
                  {m.has_att ? <span title="첨부파일">📎</span> : null}
                  {folder === 'scheduled' && <button onClick={(e) => { e.stopPropagation(); cancelSched(m.id); }} style={{ ...ghost, padding: '2px 8px' }}>예약취소</button>}
                  <span style={{ width: 96, textAlign: 'right', fontSize: 12.5, color: '#6b7280', fontWeight: 400, whiteSpace: 'nowrap' }}>{folder === 'scheduled' ? fmtDate(m.scheduled_at) : fmtDate(m.date)}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: 8, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'center', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={ghost}>◀</button>
              <span>{page} / {pages} (총 {list.total}통)</span>
              <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} style={ghost}>▶</button>
            </div>
          </>
        )}
      </div>

      {compose && settings && (
        <Compose init={compose} settings={settings} contacts={contacts} showMsg={showMsg}
          onClose={() => { setCompose(null); loadList(); loadCounts(); }}
          onDone={(closeIt) => { loadCounts(); if (closeIt) { setCompose(null); loadList(); } }} />
      )}
      {showSet && settings && <SettingsModal settings={settings} showMsg={showMsg} onClose={() => setShowSet(false)} onSaved={(b) => setSettings((s) => ({ ...s, ...b }))} />}
    </div>
  );
}
